import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, Ban, CalendarCheck, Check, CheckCircle, ChevronDown, Columns2, FileText, Link2, Package, Plus, RotateCcw, X, Zap,
} from "lucide-react";
import { BTN } from "@/components/common/btn";
import PushJobSpButton from "@/components/integrations/PushJobSpButton";
import { useAuth } from "@/auth/AuthContext";
import { STATUS_LABELS, STATUS_COLORS } from "./workOrderTabUtils";
import { WONumberEditable } from "./WOFormHeader";

// iter271 — fixed list so cancellation wording stays consistent for reporting.
const CANCEL_REASONS = [
  ["no_budget", "No budget"],
  ["lost_to_competition", "Lost to competition"],
  ["customer_cancelled", "Customer cancelled"],
  ["no_longer_required", "No longer required"],
  ["non_payment", "Non-payment"],
  ["other", "Other (describe below)"],
];

const ACTIVE = ["draft", "submitted", "approved", "on_hand", "scheduled", "in_progress", "en_route", "on_site"];

// Full-page WO header card (Jobs-style): WO# + title + customer/site on the left; status dropdown
// (drives the workflow with the same confirmations) and total on the right. Save · View · PDF · Email live on the Line items tab.
// The complete-confirm chip and the cancel-reason form render under the row when active.
export function WOHeaderCard({
  form, editingWO, fmt, total,
  clientName, siteName, siteCity,
  woSaveStatus, onBack, onRenumbered,
  handleSave, requestScheduleDate,
  confirmWOComplete, setConfirmWOComplete,
  confirmCancelWO, setConfirmCancelWO,
  setForm,
  onQuickWO, onFromProposal, onOpenWO, parentWO = null, relatedWOs = [], splitOn = false, onToggleSplit,
}) {
  const [cancelCode, setCancelCode] = useState("");
  const [cancelDetail, setCancelDetail] = useState("");
  const { can } = useAuth();
  const canPushFoundation = can("integrations.foundation_queue", "write");
  const status = form.status || "draft";
  const isActive = ACTIVE.includes(status);
  const canSchedule = !form.signature_required || editingWO?.signature_data;
  const cancelledAt = status === "cancelled" && form.cancelled_at
    ? new Date(form.cancelled_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "";

  const statusItems = [];
  if (status === "draft" || status === "submitted") statusItems.push({ key: "approved", label: "Approve", icon: CheckCircle, testId: "btn-approve-wo", run: () => handleSave("approved") });
  if (status === "approved") statusItems.push({ key: "on_hand", label: "On Hand", icon: Package, testId: "btn-on-hand-wo", run: () => handleSave("on_hand") });
  if ((status === "approved" || status === "on_hand") && canSchedule) statusItems.push({ key: "scheduled", label: "Schedule", icon: CalendarCheck, testId: "btn-schedule-wo", run: () => (form.scheduled_date ? handleSave("scheduled") : requestScheduleDate()) });
  if (isActive) statusItems.push({ key: "completed", label: "Complete", icon: Check, testId: "btn-complete-wo", run: () => setConfirmWOComplete(true) });
  if (status === "completed" || status === "cancelled") statusItems.push({ key: "draft", label: "Restore to Draft", icon: RotateCcw, testId: status === "completed" ? "btn-restore-completed-draft" : "btn-restore-draft", run: () => handleSave("draft") });

  return (
    <div className="space-y-3" data-testid="wo-header-card">
      {status === "cancelled" && (
        <div className="flex items-start gap-2.5 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3" data-testid="wo-cancelled-banner">
          <Ban className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-red-700">Cancelled{cancelledAt ? ` · ${cancelledAt}` : ""}{form.cancelled_by ? ` · ${form.cancelled_by}` : ""}</div>
            <div className="text-xs text-red-800 mt-0.5 break-words">{form.cancellation_reason || "No reason recorded."}</div>
            <div className="text-[11px] text-red-600/80 mt-1">Scheduled visits were removed from the technicians' schedules. This work order does not count as won revenue.</div>
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-card px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button type="button" onClick={onBack} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800" title="Back to work orders" data-testid="btn-wo-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <WONumberEditable form={form} woId={editingWO?.id} onRenumbered={onRenumbered} className="text-lg" />
            <h2 className="text-lg font-semibold text-slate-900 truncate" data-testid="wo-header-title">{form.title || "Untitled work order"}</h2>
          </div>
          <div className="text-xs text-slate-500 truncate" data-testid="wo-header-subtitle">
            {[clientName, siteName, siteCity].filter(Boolean).join(" · ") || "—"}
          </div>
          {(parentWO || relatedWOs.length > 0) && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1" data-testid="wo-related-chips">
              {parentWO && (
                <button type="button" onClick={() => onOpenWO?.(parentWO.id)} title={parentWO.title || ""} data-testid={`wo-related-parent-${parentWO.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100">
                  <Link2 className="w-3 h-3" /> From {parentWO.wo_number}
                </button>
              )}
              {relatedWOs.map((r) => (
                <button key={r.id} type="button" onClick={() => onOpenWO?.(r.id)} title={r.title || ""} data-testid={`wo-related-child-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 hover:bg-emerald-100">
                  <Link2 className="w-3 h-3" /> Related {r.wo_number}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {woSaveStatus && <span className="text-xs text-slate-400 italic" data-testid="wo-auto-save-status">{woSaveStatus}</span>}
          {onQuickWO && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700" title="New work order from this one" data-testid="btn-wo-new-menu">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 text-xs" data-testid="wo-new-menu">
                <DropdownMenuItem onClick={onFromProposal} data-testid="btn-wo-new-from-proposal">
                  <FileText className="w-3.5 h-3.5 mr-2" />
                  <div><div className="font-medium">From Proposal</div><div className="text-[10px] text-slate-500">Open the proposal builder for this customer &amp; site</div></div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onQuickWO} data-testid="btn-wo-new-quick">
                  <Zap className="w-3.5 h-3.5 mr-2" />
                  <div><div className="font-medium">Quick WO</div><div className="text-[10px] text-slate-500">Title, site, tech, date + a few line items</div></div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {onToggleSplit && (
            <button type="button" onClick={onToggleSplit} data-testid="wo-split-toggle" aria-pressed={!!splitOn}
              title={splitOn ? "Back to one pane" : "Split view — show two tabs side by side"}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors ${splitOn ? "border-brand bg-brand text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
              <Columns2 className="w-3.5 h-3.5" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={`inline-flex h-7 items-center gap-1 rounded-full px-3 text-xs font-medium ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`} data-testid="wo-status-badge" title="Change status">
                {STATUS_LABELS[status] || status}
                <ChevronDown className="w-3 h-3 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 text-xs" data-testid="wo-status-menu">
              {statusItems.map((it) => (
                <DropdownMenuItem key={it.key} onClick={it.run} data-testid={it.testId}>
                  <it.icon className="w-3.5 h-3.5 mr-2" />{it.label}
                </DropdownMenuItem>
              ))}
              {isActive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setConfirmCancelWO(true)} className="text-red-600 focus:text-red-700" data-testid="btn-cancel-wo">
                    <X className="w-3.5 h-3.5 mr-2" />Cancel WO
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-xl font-semibold num text-slate-900" data-testid="wo-header-total">{fmt(total)}</span>
          {canPushFoundation && editingWO?.id && ["scheduled", "completed", "invoiced", "paid"].includes(status) && (
            <PushJobSpButton woId={editingWO.id} lastStatus={form.foundation_sp_push_status || editingWO?.foundation_sp_push_status || null}
              onPushed={(res) => setForm((f) => ({ ...f, foundation_sp_push_log_id: res.log_id, foundation_sp_push_status: res.outcome }))} />
          )}
        </div>
      </div>

      {confirmWOComplete && (
        <div className="flex items-center gap-2 animate-in fade-in duration-200" data-testid="wo-complete-confirm">
          <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">Confirm 100% complete?</span>
          <Button size="sm" className={BTN.write} onClick={() => { setConfirmWOComplete(false); handleSave("completed"); }} data-testid="btn-confirm-complete-wo">Confirm</Button>
          <Button size="sm" className={BTN.nav} onClick={() => setConfirmWOComplete(false)} data-testid="btn-cancel-complete-wo">Cancel</Button>
        </div>
      )}

      {confirmCancelWO && (
        /* iter270 — a cancellation needs a reason. It lands on the WO and any not-yet-completed dispatches come off the schedule. */
        <div className="flex items-start gap-2 animate-in fade-in duration-200 flex-wrap max-w-3xl" data-testid="cancel-wo-form">
          <div className="text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded w-full">
            Cancel this work order? It moves to history as cancelled, won't count as won revenue, and any scheduled visits are removed from the technicians' schedules.
          </div>
          <Select value={cancelCode} onValueChange={setCancelCode}>
            <SelectTrigger className="h-8 text-xs w-[210px]" data-testid="select-cancel-reason"><SelectValue placeholder="Reason (required)" /></SelectTrigger>
            <SelectContent>{CANCEL_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={cancelDetail} onChange={(e) => setCancelDetail(e.target.value)}
            placeholder={cancelCode === "other" ? "Describe the reason (required)" : "Extra detail (optional)"}
            className="h-8 text-xs flex-1 min-w-[200px]" data-testid="input-cancel-reason" />
          <Button size="sm" className={`${BTN.danger} disabled:opacity-40`} disabled={!cancelCode || (cancelCode === "other" && !cancelDetail.trim())}
            onClick={() => { setConfirmCancelWO(false); handleSave("cancelled", { code: cancelCode, detail: cancelDetail.trim() }); setCancelCode(""); setCancelDetail(""); }}
            data-testid="btn-confirm-cancel-wo">
            Yes, cancel
          </Button>
          <Button size="sm" className={BTN.nav} onClick={() => { setConfirmCancelWO(false); setCancelCode(""); setCancelDetail(""); }} data-testid="btn-dismiss-cancel-wo">No</Button>
        </div>
      )}
    </div>
  );
}
